import React, { useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { ButtonText, Badge, useToast } from '@morressier/alembic';
import AuthorHero from 'src/components/AuthorHero';
import { IPeerReviewListItem } from '@publishing-engine/domain/views/review';
import { RATING_OPTIONS } from 'src/utils/reviews';
import { useRateReview } from 'src/api/commands';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import ProtectedReviewWorkflowAction from 'src/components/ProtectedAction/ProtectedReviewWorkflowAction';
import { Tooltip } from '@chakra-ui/tooltip';
import RateReviewWithEmail from './RateReviewWithEmail';
import { IEmailCommandPayload } from '@publishing-engine/domain/types/email-template';

interface Props {
  review: IPeerReviewListItem;
}

const renderReviewScore = (
  review: IPeerReviewListItem,
  displayScore?: {
    label: string;
    value: number;
  }
) => {
  const reviewAssessment =
    review.caseData.firstAssessment?.reviewScore ??
    review.caseData.revisionAssessment?.reviewScore;

  if (!reviewAssessment?.score) {
    return (
      <Text textStyle="textBody">
        You have not yet rated the review provided by{' '}
        {review.reviewer.firstName} {review.reviewer.lastName}.
      </Text>
    );
  }

  return (
    <>
      {!!displayScore && (
        <Text textStyle="textBody">
          Review scored as {displayScore.value} ({displayScore.label}).
        </Text>
      )}
      {!!reviewAssessment?.justification && (
        <>
          <Text textStyle="headingItem">Review Feedback</Text>
          <Text textStyle="textBody" wordBreak="break-word">
            "{reviewAssessment?.justification}"
          </Text>
        </>
      )}
    </>
  );
};

const AboutReview: React.FC<Props> = ({ review }) => {
  const { submissionContextId, organizationId } = useSubmissionData();

  const { firstAssessment } = review.caseData;

  const reviewAssessment =
    firstAssessment?.reviewScore ??
    review.caseData.revisionAssessment?.reviewScore;

  const revisionExists = !!review.caseData.revisionAssessment;
  const receiveFirstAssessmentFeedback = !revisionExists
    ? firstAssessment?.receiveFeedback
    : '';

  // state
  const [showRateReviewModal, setShowRateReviewModal] = useState(false);

  const toast = useToast();
  const submissionId = review.submission.id;
  const { mutateAsync: mutateRateReview } = useRateReview({
    submissionId,
    organizationId,
    reviewId: review.id,
    submissionContextId,
    reviewerId: review.reviewer.id,
    queryKey: [
      'submission-context',
      submissionContextId,
      submissionId,
      'reviews'
    ] // fetch reviews query key
  });

  const handleRateReview = async (
    rating: string,
    justification?: string,
    emails?: IEmailCommandPayload
  ) => {
    const response = await mutateRateReview({
      reviewScore: { score: rating, justification },
      emails
    });
    if (response.isSuccess) {
      const ratingOption = RATING_OPTIONS[rating];
      toast({
        variant: 'success',
        title: `Review Rated as ${ratingOption?.value} (${ratingOption?.label})`,
        description:
          receiveFirstAssessmentFeedback === 'yes'
            ? 'The Reviewer will receive the results once a decision for this Submission is made.'
            : ''
      });
      setShowRateReviewModal(false);
    } else {
      toast({
        variant: 'error',
        title: 'Error Rating Review'
      });
    }
  };

  const displayScore = RATING_OPTIONS[reviewAssessment?.score || ''];

  return (
    <>
      {showRateReviewModal && (
        <RateReviewWithEmail
          reviewId={review.id}
          reviewRequestId={review.reviewer.id}
          email={review.reviewer.email}
          onClose={() => setShowRateReviewModal(false)}
          onRateReview={handleRateReview}
          receiveFeedback={receiveFirstAssessmentFeedback}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
        />
      )}
      <Flex
        gap={8}
        background="whiteSecondaryFill"
        marginTop={4}
        flexDirection="column"
      >
        <Flex alignItems="flex-start" justifyContent="space-between">
          <Flex gap={2} width="100%">
            <Flex
              flex="1"
              flexDirection="column"
              gap={2}
              alignItems="flex-start"
            >
              <AuthorHero reviewer={review.reviewer} showAvatar={false} />
              {!!displayScore && (
                <Badge variant="success" marginRight={4}>
                  R-score : {displayScore.value}
                </Badge>
              )}
            </Flex>
            {review.coReview?.status === 'accepted' &&
              review.coReview.coReviewer && (
                <Flex
                  flex="1"
                  flexDirection="column"
                  gap={2}
                  alignItems="flex-start"
                >
                  <AuthorHero
                    reviewer={{
                      ...review.coReview.coReviewer,
                      firstName: review.coReview.coReviewer.firstName || '',
                      lastName: review.coReview.coReviewer.lastName || '',
                      affiliationDetails: {
                        organizationName:
                          review.coReview.coReviewer.affiliation || '',
                        ringgoldId: review.coReview.coReviewer.ringgoldId || ''
                      }
                    }}
                    showAvatar={false}
                  />
                  {!!displayScore && (
                    <Badge variant="success" marginRight={4}>
                      R-score : {displayScore.value}
                    </Badge>
                  )}
                </Flex>
              )}
          </Flex>
        </Flex>
        <Flex>
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">Submission Review Score</Text>
            {renderReviewScore(review, displayScore)}
            <Flex>
              <ProtectedReviewWorkflowAction
                actionName="RateReview"
                reviewId={review.id}
              >
                <Tooltip
                  label="Already rated this review"
                  isDisabled={!displayScore?.value}
                  shouldWrapChildren
                  placement="top"
                >
                  <ButtonText
                    onClick={() => setShowRateReviewModal(true)}
                    isDisabled={
                      review.isUserDeactivated ||
                      !review.canRateReview ||
                      !!displayScore?.value
                    }
                  >
                    Rate This Review
                  </ButtonText>
                </Tooltip>
              </ProtectedReviewWorkflowAction>
            </Flex>
          </Flex>
        </Flex>

        {receiveFirstAssessmentFeedback && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">Feedback Request</Text>
            <Text textStyle="textBody">
              {receiveFirstAssessmentFeedback === 'yes'
                ? 'Yes: The reviewer requested feedback on their review to improve future submissions.'
                : 'No: The reviewer declined to receive feedback on their review.'}
            </Text>
          </Flex>
        )}
      </Flex>
    </>
  );
};

export default AboutReview;
